/* Acqua di Volterra — theme.js
   Minimal vanilla JS: dialogs (menu, cart), Ajax cart, variant picker, sticky buy bar.
   Everything degrades to plain links and forms without JavaScript. */
(() => {
  const config = window.AVO || { routes: {}, strings: {} };
  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  /* ---------- Dialogs ---------- */
  const lockScroll = (locked) => document.documentElement.classList.toggle('is-locked', locked);

  const openDialog = (dialog) => {
    if (!dialog || dialog.open) return;
    dialog.showModal();
    lockScroll(true);
    qsa(`[aria-controls="${dialog.id}"]`).forEach((el) => el.setAttribute('aria-expanded', 'true'));
  };

  const closeDialog = (dialog) => {
    if (dialog && dialog.open) dialog.close();
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-dialog-open]');
    if (opener) {
      event.preventDefault();
      openDialog(document.getElementById(opener.dataset.dialogOpen));
      return;
    }
    const closer = event.target.closest('[data-dialog-close]');
    if (closer) {
      closeDialog(closer.closest('dialog'));
      return;
    }
    // Click on the backdrop closes the dialog.
    if (event.target.matches('dialog[data-dialog]')) closeDialog(event.target);
  });

  document.addEventListener(
    'close',
    (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement)) return;
      if (!qs('dialog[open]')) lockScroll(false);
      qsa(`[aria-controls="${dialog.id}"]`).forEach((el) => el.setAttribute('aria-expanded', 'false'));
    },
    true
  );

  // Close the mobile menu when the viewport becomes desktop.
  window.matchMedia('(min-width: 1200px)').addEventListener('change', (event) => {
    if (event.matches) qsa('.menu-drawer[open]').forEach(closeDialog);
  });

  /* ---------- Cart ---------- */
  const cartDrawer = () => qs('#CartDrawer');
  const useDrawer = () => config.cartType === 'drawer' && !!cartDrawer();

  const updateCartCount = (count) => {
    qsa('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
    const template = qs('#CartDrawer [data-count-label]');
    if (template) {
      qsa('[data-cart-count-label]').forEach((el) => {
        el.textContent = template.dataset.countLabel;
      });
    }
  };

  const renderCartDrawer = (sections) => {
    const html = sections && sections['cart-drawer'];
    if (!html) return;
    const fresh = qs('[data-cart-drawer-inner]', new DOMParser().parseFromString(html, 'text/html'));
    const current = qs('[data-cart-drawer-inner]');
    if (fresh && current) current.replaceWith(fresh);
    const inner = qs('[data-cart-drawer-inner]');
    if (inner) updateCartCount(Number(inner.dataset.itemCount || 0));
  };

  const postJSON = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: ['cart-drawer'], sections_url: window.location.pathname }),
    });
    const data = await response.json();
    if (!response.ok) throw data;
    return data;
  };

  const showCartError = (message) => {
    const region = qs('#CartDrawer [data-cart-error]');
    if (region) region.textContent = message || config.strings.cartError;
  };

  const changeLine = async (line, quantity) => {
    const inner = qs('[data-cart-drawer-inner]');
    if (inner) inner.setAttribute('aria-busy', 'true');
    try {
      const data = await postJSON(config.routes.cartChange, { line, quantity });
      renderCartDrawer(data.sections);
      const focusTarget = qs(`#CartDrawer [data-line="${line}"] [data-quantity-input]`) || qs('#CartDrawer .drawer__body');
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    } catch (error) {
      showCartError(error.description || error.message);
    } finally {
      const after = qs('[data-cart-drawer-inner]');
      if (after) after.removeAttribute('aria-busy');
    }
  };

  // Open the drawer from the header cart icon.
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-cart-open]');
    if (!trigger || !useDrawer()) return;
    event.preventDefault();
    openDialog(cartDrawer());
  });

  // Quantity buttons and remove links inside the drawer.
  document.addEventListener('click', (event) => {
    const button = event.target.closest('#CartDrawer [data-quantity-change], #CartDrawer [data-line-remove]');
    if (!button) return;
    event.preventDefault();
    const item = button.closest('[data-line]');
    const line = Number(item.dataset.line);
    if (button.hasAttribute('data-line-remove')) {
      changeLine(line, 0);
      return;
    }
    const input = qs('[data-quantity-input]', item);
    const next = Math.max(0, Number(input.value) + Number(button.dataset.quantityChange));
    changeLine(line, next);
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('#CartDrawer [data-quantity-input]');
    if (input) {
      const item = input.closest('[data-line]');
      changeLine(Number(item.dataset.line), Math.max(0, Number(input.value)));
    }
  });

  // Cart page: quantity changes submit the form automatically (no "update" click needed).
  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cart-page-form] [data-quantity-input]');
    if (!input) return;
    const form = input.closest('form');
    const hidden = document.createElement('input');
    hidden.type = 'hidden';
    hidden.name = 'update';
    hidden.value = '1';
    form.appendChild(hidden);
    form.submit();
  });

  // Gift note (cart.note), saved when the field loses focus or after typing pauses.
  let noteTimer;
  document.addEventListener('input', (event) => {
    const note = event.target.closest('[data-cart-note]');
    if (!note) return;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => {
      fetch(config.routes.cartUpdate, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: note.value }),
      });
    }, 400);
  });

  // Product forms: add via Ajax and open the drawer. Without a drawer the form posts normally.
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-product-form]');
    if (!form || !useDrawer()) return;
    event.preventDefault();

    const buttons = qsa('button[type="submit"]').filter((button) => button.form === form);
    const errorRegion = qs('[data-form-error]', form.closest('[data-product], .product-tile, .bundle') || form);
    buttons.forEach((button) => {
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
    });
    if (errorRegion) errorRegion.textContent = '';

    try {
      const body = new FormData(form);
      body.append('sections', 'cart-drawer');
      body.append('sections_url', window.location.pathname);
      const response = await fetch(config.routes.cartAdd, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const data = await response.json();
      if (!response.ok) throw data;
      renderCartDrawer(data.sections);
      openDialog(cartDrawer());
    } catch (error) {
      if (errorRegion) errorRegion.textContent = error.description || error.message || config.strings.cartError;
    } finally {
      buttons.forEach((button) => {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
      });
    }
  });

  /* ---------- Variant picker ---------- */
  const initProduct = (root) => {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';

    const dataScript = qs('[data-product-json]', root);
    if (!dataScript) return;
    const variants = JSON.parse(dataScript.textContent);
    const idInput = qs('[data-variant-id]', root);
    const updateUrl = root.dataset.updateUrl === 'true';

    const selectedOptions = () =>
      qsa('[data-option-index]', root).map((fieldset) => {
        const checked = qs('input:checked', fieldset);
        return checked ? checked.value : null;
      });

    const setButtons = (variant) => {
      qsa('[data-add-button]', root).forEach((button) => {
        const label = qs('[data-add-label]', button);
        const available = variant && variant.available;
        button.disabled = !available;
        if (label && root.dataset.coming === 'true') {
          label.textContent = config.strings.comingSoon;
        } else if (label) {
          const addText = button.dataset.textAdd || config.strings.addToCart;
          label.textContent = !variant ? config.strings.unavailable : available ? addText : config.strings.soldOut;
        }
      });
      // "Notify me" follows the selected size: shown while it can't be bought, tagged with that size
      qsa('[data-notify]', root).forEach((notify) => {
        notify.hidden = Boolean(variant && variant.available) && root.dataset.coming !== 'true';
        const tags = qs('[data-notify-tags]', notify);
        if (tags && variant && variants.length > 1) {
          const size = variant.options.join('-').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
          tags.value = `avvisami, ${notify.dataset.notifyBase}-${size}`;
        }
      });
    };

    const swapFromTemplate = (selector, variantId) => {
      const template = qs(`template[data-${selector}-for="${variantId}"]`, root);
      qsa(`[data-${selector}]`, root).forEach((target) => {
        if (template) target.innerHTML = template.innerHTML;
      });
    };

    // Photos that belong to a size: only the selected size's photo is shown
    const sizeMedia = new Set(variants.map((v) => v.media_id).filter(Boolean).map(String));

    const moveMedia = (mediaId) => {
      if (!mediaId) return;
      const gallery = qs('[data-gallery]', root);
      const item = gallery && qs(`[data-media-id="${mediaId}"]`, gallery);
      if (!item) return;
      qsa('[data-media-id]', gallery).forEach((other) => {
        if (other !== item && sizeMedia.has(other.dataset.mediaId)) {
          other.hidden = true;
          gallery.append(other);
        }
      });
      item.hidden = false;
      gallery.prepend(item);
      gallery.scrollTo({ left: 0, behavior: 'smooth' });
    };

    const onChange = () => {
      const options = selectedOptions();
      const variant = variants.find((v) => v.options.every((value, index) => value === options[index]));

      setButtons(variant);
      if (!variant) return;

      idInput.value = variant.id;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
      swapFromTemplate('price', variant.id);
      swapFromTemplate('availability', variant.id);
      qsa('[data-selected-option]', root).forEach((el) => {
        el.textContent = options[Number(el.dataset.selectedOption)] || '';
      });
      moveMedia(variant.media_id);

      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      document.dispatchEvent(
        new CustomEvent('avo:variant-change', { detail: { variantId: variant.id, productId: root.dataset.productId } })
      );
    };

    root.addEventListener('change', (event) => {
      if (event.target.matches('[data-option-index] input')) onChange();
    });
  };

  // Content blocks elsewhere on the page that depend on the selected variant (e.g. "Cosa ricevi").
  document.addEventListener('avo:variant-change', (event) => {
    const { variantId, productId } = event.detail;
    qsa(`[data-variant-content][data-product-id="${productId}"]`).forEach((group) => {
      const match = qs(`[data-variant-id-for="${variantId}"]`, group);
      if (!match) return;
      qsa('[data-variant-id-for]', group).forEach((el) => {
        el.hidden = el !== match;
      });
    });
  });

  /* ---------- Disclosures (language/country switch): close on outside click and Escape ---------- */
  document.addEventListener('click', (event) => {
    qsa('details[data-disclosure][open]').forEach((el) => {
      if (!el.contains(event.target)) el.removeAttribute('open');
    });
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    qsa('details[data-disclosure][open]').forEach((el) => {
      el.removeAttribute('open');
      qs('summary', el)?.focus();
    });
  });

  /* ---------- Country choice brings its language along (Italy: Italian, DACH: German, others: English) ---------- */
  document.addEventListener('change', (event) => {
    const select = event.target.closest('select[name="country_code"]');
    const field = select && qs('[data-country-language]', select.form);
    if (!field) return;
    const language = select.selectedOptions[0]?.dataset.language || '';
    field.value = language;
    field.disabled = !language;
  });
  // A language button wins over the country's language
  document.addEventListener('submit', (event) => {
    const field = qs('[data-country-language]', event.target);
    if (field && event.submitter?.name === 'language_code') field.disabled = true;
  });

  /* ---------- Horizontal strips with arrow buttons (Instagram) ---------- */
  qsa('[data-scroller]').forEach((root) => {
    const track = qs('[data-scroller-track]', root);
    const arrows = qs('[data-scroller-arrows]', root);
    const prev = qs('[data-scroller-prev]', root);
    const next = qs('[data-scroller-next]', root);
    if (!track || !arrows) return;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    const step = () => (track.firstElementChild ? track.firstElementChild.getBoundingClientRect().width + 8 : track.clientWidth);
    const update = () => {
      const overflow = track.scrollWidth - track.clientWidth > 2;
      arrows.hidden = !overflow;
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    };
    prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior }));
    next.addEventListener('click', () => track.scrollBy({ left: step(), behavior }));
    track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
    window.addEventListener('resize', update);
    update();
  });

  /* ---------- Gallery dots (mobile scroll-snap) ---------- */
  const initGalleryDots = (root) => {
    const gallery = qs('[data-gallery]', root);
    const dots = qsa('[data-gallery-dots] span', root);
    if (!gallery || dots.length < 2) return;
    let ticking = false;
    gallery.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const index = Math.round(gallery.scrollLeft / gallery.clientWidth);
          dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
        });
      },
      { passive: true }
    );
  };

  /* ---------- Sticky buy bar (mobile) ---------- */
  const initSticky = (root) => {
    const bar = qs('[data-sticky-buy]', root);
    const target = qs('[data-main-add]', root);
    if (!bar || !target) return;
    const footer = qs('.section-footer');
    // A scroll check instead of IntersectionObserver: anchor jumps can skip the
    // button entirely, and an observer only fires when a threshold is crossed.
    let ticking = false;
    const update = () => {
      ticking = false;
      const buttonAbove = target.getBoundingClientRect().bottom < 0;
      const footerVisible = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
      const show = buttonAbove && !footerVisible;
      bar.classList.toggle('is-visible', show);
      bar.inert = !show;
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  };

  /* ---------- Cookie preferences button ---------- */
  // Shopify loads its privacy banner asynchronously and only where it is active.
  // Show the round button once privacyBanner exists; until then (or if it never
  // loads) the footer keeps Shopify's own text link.
  const initCookieButton = () => {
    const button = qs('[data-cookie-button]');
    if (!button) return;
    let tries = 0;
    const check = () => {
      const banner = window.privacyBanner;
      if (banner && typeof banner.showPreferences === 'function') {
        button.hidden = false;
        // Hide the footer's cookie text link, whether Shopify lists it with the
        // policies or injects it later.
        const hideTextLink = () => {
          qsa('.footer__policies').forEach((list) => {
            list.classList.add('has-cookie-button');
            qsa('li', list).forEach((item) => {
              const link = qs('a, button', item);
              if (!link) return;
              const href = link.getAttribute('href') || '';
              if (/cookie|privacy-choices/i.test(href) || /cookie/i.test(link.textContent)) item.hidden = true;
            });
          });
        };
        hideTextLink();
        setTimeout(hideTextLink, 2000);
        button.addEventListener('click', () => banner.showPreferences());
        return;
      }
      tries += 1;
      if (tries < 20) setTimeout(check, 500);
    };
    check();
  };

  /* ---------- Init ---------- */
  const init = (scope = document) => {
    qsa('[data-product]', scope).forEach((root) => {
      initProduct(root);
      initSticky(root);
      initGalleryDots(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
      initCookieButton();
    }, { once: true });
  } else {
    init();
    initCookieButton();
  }

  // Theme editor support.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:select', (event) => {
    if (event.target.querySelector('#CartDrawer')) openDialog(cartDrawer());
  });
})();
